import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import AnnouncementBar from '../components/AnnouncementBar';
import Footer from '../components/Footer';
import SponsorsSection from '../components/SponsorsSection';
import ScrollToTop from '../components/ScrollToTop';
import TornPaperWrapper from '../components/TornPaperWrapper';
import './TicketsFunnel.css';
// Contact.css provides the ``partner-contact-info`` styles reused by the
// vendor-contact block rendered below the two funnel cards. Keeping the
// same class + inline-styles used on /contact and /sponsors ensures the
// card looks byte-identical across the three pages it appears on.
import './Contact.css';
import { getCmsImagePath } from '../cms/cmsData';
import { getArabicTranslation } from '../utils/arabicTranslations';

const resolveLetter = (name) => {
  const firstChar = (name || '').trim().charAt(0).toUpperCase();
  return /^[A-Z]$/.test(firstChar) ? firstChar : '#';
};

const splitGroupsIntoBalancedColumns = (groups, columnCount = 3) => {
  const columns = Array.from({ length: columnCount }, () => ({ weight: 0, groups: [] }));
  groups.forEach((group) => {
    const lightest = columns.reduce((best, col, idx) => (
      col.weight < columns[best].weight ? idx : best
    ), 0);
    columns[lightest].groups.push(group);
    columns[lightest].weight += (group.vendors?.length || 0) + 1;
  });
  return columns.map((c) => c.groups);
};

const VendorFunnel = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [hasTicketsInCart, setHasTicketsInCart] = useState(false);
  const [hasVendorInCart, setHasVendorInCart] = useState(false);
  const [vendorBlocked, setVendorBlocked] = useState(false); // email already has pending/approved vendor
  const [blockReason, setBlockReason] = useState('');
  const [vendors, setVendors] = useState([]);
  const [vendorsLoading, setVendorsLoading] = useState(true);
  const [vendorsError, setVendorsError] = useState('');
  const [vendorTypeFilter, setVendorTypeFilter] = useState('all');

  // Check cart state
  useEffect(() => {
    const checkCart = () => {
      try {
        const stored = localStorage.getItem('cart');
        if (stored) {
          const parsed = JSON.parse(stored);
          const items = parsed.items || [];
          setHasTicketsInCart(items.some(item => 
            item.type === 'festival' || item.type === 'amphitheater' || item.sectionId || item.holdIds
          ));
          setHasVendorInCart(items.some(item => item.type === 'vendor-booth'));
        } else {
          setHasTicketsInCart(false);
          setHasVendorInCart(false);
        }
      } catch (e) {
        setHasTicketsInCart(false);
        setHasVendorInCart(false);
      }
    };
    checkCart();
    window.addEventListener('cartUpdated', checkCart);
    window.addEventListener('storage', checkCart);
    return () => {
      window.removeEventListener('cartUpdated', checkCart);
      window.removeEventListener('storage', checkCart);
    };
  }, []);

  // Check vendor eligibility for logged-in users
  useEffect(() => {
    if (!isAuthenticated || !user?.email) {
      setVendorBlocked(false);
      return;
    }
    const checkEligibility = async () => {
      try {
        const res = await api.checkVendorEligibility(user.email);
        if (res?.success && !res.eligible) {
          setVendorBlocked(true);
          setBlockReason(res.reason === 'approved'
            ? 'Your vendor booth has already been approved. You cannot purchase another vendor booth with this email.'
            : 'You already have a pending vendor booth application. You cannot purchase another vendor booth until it is processed.');
        } else {
          setVendorBlocked(false);
        }
      } catch (err) {
        console.error('Vendor eligibility check failed:', err);
      }
    };
    checkEligibility();
  }, [isAuthenticated, user]);

  useEffect(() => {
    let mounted = true;
    const fetchVendors = async () => {
      setVendorsLoading(true);
      setVendorsError('');
      try {
        const res = await api.getPublicVendors();
        if (!mounted) return;
        if (res?.success) {
          const list = Array.isArray(res.data) ? res.data : [];
          setVendors(list);
        } else {
          setVendors([]);
          setVendorsError(res?.error?.message || 'Unable to load approved vendors right now.');
        }
      } catch (err) {
        if (!mounted) return;
        setVendors([]);
        setVendorsError(err?.message || 'Unable to load approved vendors right now.');
      } finally {
        if (mounted) setVendorsLoading(false);
      }
    };
    fetchVendors();
    return () => { mounted = false; };
  }, []);

  const filteredVendors = useMemo(() => {
    if (vendorTypeFilter === 'all') return vendors;
    return vendors.filter((v) => (v.vendor_type || '').toLowerCase() === vendorTypeFilter);
  }, [vendors, vendorTypeFilter]);

  const vendorColumns = useMemo(() => {
    if (!filteredVendors.length) return [[], [], []];
    const grouped = filteredVendors.reduce((acc, vendor) => {
      const displayName = (vendor.booth_display_name || vendor.business_name || '').trim();
      if (!displayName) return acc;
      const letter = resolveLetter(displayName);
      if (!acc[letter]) acc[letter] = [];
      acc[letter].push(vendor);
      return acc;
    }, {});
    const letters = Object.keys(grouped).sort((a, b) => (a === '#' ? 1 : b === '#' ? -1 : a.localeCompare(b)));
    const groups = letters.map((letter) => ({
      letter,
      vendors: grouped[letter].sort((a, b) => {
        const an = (a.booth_display_name || a.business_name || '').toLowerCase();
        const bn = (b.booth_display_name || b.business_name || '').toLowerCase();
        return an.localeCompare(bn);
      }),
    }));
    return splitGroupsIntoBalancedColumns(groups, 3);
  }, [filteredVendors]);

  const handleClearCart = () => {
    localStorage.removeItem('cart');
    window.dispatchEvent(new CustomEvent('cartUpdated', { detail: { items: [], total: 0 } }));
    setHasVendorInCart(false);
    setHasTicketsInCart(false);
  };

  return (
    <div className="tickets-funnel-wrapper">
      <AnnouncementBar />

      <section className="tickets-funnel-hero">
        <div className="hero-background-wrapper">
          <video src={"/hero-final.mp4"} alt="oc mena festival" className="hero-background-video" autoPlay muted loop playsInline />
          <div className="hero-gradient-overlay"></div>
        </div>

        <TornPaperWrapper>
          <h1 className="funnel-heading">
            Vendors
            <span className="arabic-text on-white">{getArabicTranslation('Vendors')}</span>
          </h1>

          {/* Vendor blocked warning (logged in user with existing vendor order) */}
          {vendorBlocked && !hasVendorInCart && (
            <div style={{ textAlign: 'center', marginBottom: '24px', maxWidth: '900px', marginLeft: 'auto', marginRight: 'auto' }}>
              <span className="funnel-option-badge" style={{ fontSize: '13px', padding: '10px 18px', lineHeight: '1.6', whiteSpace: 'normal', maxWidth: '600px', display: 'inline-block', background: '#fdf2c6', color: '#d87706' }}>
                {blockReason}
              </span>
            </div>
          )}

          <div className="funnel-cards">
            {/* Bazaar Vendor Card */}
            <div className="funnel-card">
              <span className="funnel-option-badge">Option 1</span>
              <h2 className="funnel-card-title">
                Bazaar Vendors
                <span className="arabic-text on-white arabic-below">{getArabicTranslation('Bazaar Vendors')}</span>
              </h2>

              <div className="funnel-card-image">
                <img loading="lazy" src={getCmsImagePath("vendorBazaarCard", "/bazaar.webp")} alt="Bazaar Vendors" />
              </div>

              <p className="funnel-card-description">
                Showcase your products in our vibrant bazaar marketplace. Connect with thousands of attendees, build brand visibility, and sell directly in a high-energy festival atmosphere all weekend.
              </p>

              <span className="funnel-cta-btn" style={{ background: '#E1E7EF', borderColor: '#E1E7EF', color: '#6b7280', cursor: 'not-allowed', pointerEvents: 'none' }}>
                SOLD OUT
              </span>
            </div>

            {/* Food Vendor Card */}
            <div className="funnel-card">
              <span className="funnel-option-badge">Option 2</span>
              <h2 className="funnel-card-title">
                Food/Beverage Vendors
                <span className="arabic-text on-white">{getArabicTranslation('Food/Beverage Vendors')}</span>
              </h2>

              <div className="funnel-card-image">
                <img loading="lazy" src={getCmsImagePath("vendorFoodCard", "/food.webp")} alt="Food Vendors" />
              </div>

              <p className="funnel-card-description">
                Serve fresh flavors to a hungry crowd in our dedicated food section for booths and food trucks. Enjoy strong visibility, steady foot traffic, and an energetic festival atmosphere.
              </p>

              <span className="funnel-cta-btn" style={{ background: '#E1E7EF', borderColor: '#E1E7EF', color: '#6b7280', cursor: 'not-allowed', pointerEvents: 'none' }}>
                SOLD OUT
              </span>
            </div>
          </div>



          <div className="vendor-directory-section">
            {hasTicketsInCart && !hasVendorInCart && (
              <div style={{ textAlign: 'center', marginBottom: '20px', maxWidth: '900px', marginLeft: 'auto', marginRight: 'auto' }}>
                <span className="funnel-option-badge" style={{ fontSize: '13px', padding: '10px 18px', lineHeight: '1.6', whiteSpace: 'normal', maxWidth: '700px', display: 'inline-block' }}>
                  If you register for a vendor booth, any festival or amphitheater tickets in your cart will be removed before payment is processed. You can purchase tickets after or before, but not at the same time.
                </span>
              </div>
            )}

            <h3 className="vendor-directory-heading">
              Vendors that are coming to the Festival!
              <span className="arabic-text on-white arabic-below">{getArabicTranslation('Vendors that are coming to the Festival')}</span>
            </h3>
            <p className="vendor-directory-text">We're curating 200+ food and bazaar stalls across three days at the OC Fairgrounds. View some of our approved vendors below.</p>

            <div className="vendor-type-toggle" role="tablist" aria-label="Filter vendor type">
              <button
                type="button"
                className={`vendor-type-pill ${vendorTypeFilter === 'all' ? 'active' : ''}`}
                onClick={() => setVendorTypeFilter('all')}
              >
                All
              </button>
              <button
                type="button"
                className={`vendor-type-pill ${vendorTypeFilter === 'food' ? 'active' : ''}`}
                onClick={() => setVendorTypeFilter('food')}
              >
                Food
              </button>
              <button
                type="button"
                className={`vendor-type-pill ${vendorTypeFilter === 'bazaar' ? 'active' : ''}`}
                onClick={() => setVendorTypeFilter('bazaar')}
              >
                Bazaar
              </button>
            </div>

            {vendorsLoading && <p className="vendor-list-state">Loading approved vendors…</p>}
            {!vendorsLoading && vendorsError && <p className="vendor-list-state">{vendorsError}</p>}
            {!vendorsLoading && !vendorsError && filteredVendors.length === 0 && (
              <p className="vendor-list-state">Approved vendors will appear here soon.</p>
            )}

            {!vendorsLoading && !vendorsError && filteredVendors.length > 0 && (
              <div className="vendor-alpha-grid">
                {vendorColumns.map((column, colIdx) => (
                  <div className="vendor-alpha-column" key={`vendor-col-${colIdx}`}>
                    {column.map((group) => (
                      <div className="vendor-letter-group" key={group.letter}>
                        <h4 className="vendor-letter-heading">{group.letter}</h4>
                        <ul className="vendor-letter-list">
                          {group.vendors.map((vendor) => {
                            const name = vendor.booth_display_name || vendor.business_name;
                            return (
                              <li key={vendor.id}>
                                <span className="vendor-link vendor-link-nohref">{name}</span>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Vendor in cart warning - shown below cards */}
          {hasVendorInCart && (
            <div style={{ textAlign: 'center', marginTop: '24px', maxWidth: '900px', marginLeft: 'auto', marginRight: 'auto' }}>
              <span className="funnel-option-badge" style={{ fontSize: '13px', padding: '10px 18px', lineHeight: '1.6', whiteSpace: 'normal', maxWidth: '600px', display: 'inline-block', background: '#fdf2c6', color: '#d87706' }}>
                You already have a vendor product in your cart. You may purchase only one vendor product per email address. If you selected the wrong vendor product, please remove it from your cart and add a different one, or continue to checkout.
              </span>
              <div style={{ display: 'flex', justifyContent: 'center', gap: '12px', marginTop: '16px' }}>
                <button
                  onClick={handleClearCart}
                  style={{ 
                    padding: '12px 28px', 
                    fontSize: '14px', 
                    fontWeight: 600, 
                    borderRadius: '30px', 
                    border: '2px solid #111', 
                    background: '#fff', 
                    color: '#111', 
                    cursor: 'pointer', 
                    textAlign: 'center',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  Clear Cart
                </button>
                <button
                  onClick={() => navigate('/cart')}
                  className="funnel-cta-btn"
                  style={{ padding: '12px 28px', fontSize: '14px', fontWeight: 600, borderRadius: '30px', border: 'none', cursor: 'pointer', display: 'inline-block' }}
                >
                  Checkout
                </button>
              </div>
            </div>
          )}

        </TornPaperWrapper>

        <div className="lanterns-container">
          <img loading="lazy" src={getCmsImagePath("lanterns", "/lanterns.webp")} alt="festival lanterns" className="lanterns-image" />
        </div>
      </section>

      <SponsorsSection />
      <Footer />
      <ScrollToTop />
    </div>
  );
};

export default VendorFunnel;
